import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  RobotIcon,
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  DatabaseIcon,
  CloudIcon,
  CodeIcon,
  DesktopIcon,
  MobileAltIcon,
  LightbulbIcon,
} from "./IconComponents";
import SEO from "./SEO";
import RelatedServices from "./RelatedServices";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: var(--text-color);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const ServiceTypesSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ServiceTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServiceTypeCard = styled.div`
  background: var(--light-background);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-5px);
  }
`;

const ServiceTypeTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ServiceTypeDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ServiceTypeFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--text-color);
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: "✓";
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  padding: 3rem;
  background-color: var(--card-background);
  border-radius: 8px;
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const KI: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <RocketIcon />,
      title: "Wettbewerbsvorteil durch KI",
      description:
        "Nutzen Sie künstliche Intelligenz, um schneller, präziser und datengetriebener Entscheidungen zu treffen als Ihre Mitbewerber.",
    },
    {
      icon: <LockIcon />,
      title: "Höhere Genauigkeit",
      description:
        "KI-Modelle erkennen Muster und Zusammenhänge, die für Menschen oft unsichtbar bleiben, und liefern präzisere Ergebnisse.",
    },
    {
      icon: <UsersIcon />,
      title: "Entlastung Ihrer Mitarbeiter",
      description:
        "Automatisieren Sie repetitive und zeitaufwändige Aufgaben mit KI, damit sich Ihr Team auf strategische Themen konzentrieren kann.",
    },
    {
      icon: <LightbulbIcon />,
      title: "Neue Geschäftsmodelle",
      description:
        "KI eröffnet neue Möglichkeiten für Produkte, Services und Prozesse, die vorher nicht denkbar waren.",
    },
  ];

  const process = [
    {
      title: "Bedarfsanalyse",
      description:
        "Wir identifizieren gemeinsam mit Ihnen die Bereiche mit dem größten KI-Potenzial in Ihrem Unternehmen.",
    },
    {
      title: "Modellentwicklung",
      description:
        "Entwicklung und Training maßgeschneiderter KI- und Machine-Learning-Modelle für Ihre spezifischen Anforderungen.",
    },
    {
      title: "Integration",
      description:
        "Nahtlose Einbindung der KI-Lösung in Ihre bestehende IT-Landschaft und Geschäftsprozesse.",
    },
    {
      title: "Monitoring & Optimierung",
      description:
        "Kontinuierliche Überwachung, Nachtrainierung und Optimierung der Modelle für dauerhaft hohe Qualität.",
    },
  ];

  const benefits = [
    {
      title: "Kosteneffizienz",
      description:
        "Reduzieren Sie Betriebskosten durch intelligente Automatisierung und optimierte Ressourcennutzung.",
    },
    {
      title: "Schnellere Entscheidungen",
      description:
        "Echtzeit-Analysen und Prognosen ermöglichen fundierte Entscheidungen in Sekundenschnelle.",
    },
    {
      title: "Skalierbarkeit",
      description:
        "KI-Lösungen lassen sich flexibel an wachsende Datenmengen und steigende Anforderungen anpassen.",
    },
    {
      title: "Personalisierung",
      description:
        "Bieten Sie Ihren Kunden individuelle Erlebnisse durch KI-gestützte Personalisierung.",
    },
    {
      title: "Risikominimierung",
      description:
        "Frühzeitige Erkennung von Anomalien, Betrug und Risiken durch intelligente Überwachungssysteme.",
    },
    {
      title: "Innovationskraft",
      description:
        "Bleiben Sie mit modernster KI-Technologie an der Spitze der digitalen Transformation.",
    },
  ];

  const technologies = [
    {
      icon: <RobotIcon />,
      name: "Machine Learning",
      description: "Scikit-learn, TensorFlow, PyTorch",
    },
    {
      icon: <CodeIcon />,
      name: "Natural Language Processing",
      description: "GPT-Modelle, Sprachverarbeitung, Chatbots",
    },
    {
      icon: <CloudIcon />,
      name: "Cloud KI-Dienste",
      description: "Azure AI, AWS AI/ML, Google Cloud AI",
    },
    {
      icon: <DatabaseIcon />,
      name: "Datenanalyse",
      description: "Big Data, Data Mining, Predictive Analytics",
    },
    {
      icon: <DesktopIcon />,
      name: "Computer Vision",
      description: "Bilderkennung, Objekterkennung, Qualitätskontrolle",
    },
    {
      icon: <MobileAltIcon />,
      name: "KI in Apps",
      description: "Intelligente mobile und Web-Anwendungen",
    },
  ];

  const caseStudies = [
    {
      title: "Intelligenter Kundenservice-Chatbot",
      description:
        "Entwicklung eines KI-gestützten Chatbots für ein E-Commerce-Unternehmen zur automatischen Beantwortung von Kundenanfragen rund um die Uhr.",
      result: {
        title: "Ergebnisse:",
        description:
          "70% weniger Support-Tickets, 24/7 Verfügbarkeit, 45% schnellere Antwortzeiten, deutlich höhere Kundenzufriedenheit",
      },
    },
    {
      title: "Predictive Maintenance in der Fertigung",
      description:
        "Implementierung eines KI-Modells zur vorausschauenden Wartung von Maschinen basierend auf Sensordaten und historischen Ausfallmustern.",
      result: {
        title: "Ergebnisse:",
        description:
          "50% weniger ungeplante Ausfallzeiten, 30% geringere Wartungskosten, verlängerte Maschinenlebensdauer",
      },
    },
    {
      title: "KI-gestützte Dokumentenanalyse",
      description:
        "Automatische Extraktion und Klassifizierung relevanter Informationen aus großen Mengen unstrukturierter Dokumente mittels NLP.",
      result: {
        title: "Ergebnisse:",
        description:
          "85% Zeitersparnis bei der Dokumentenverarbeitung, 99% Genauigkeit, signifikante Kostenreduktion",
      },
    },
  ];

  const serviceTypes = [
    {
      title: "KI-Beratung & Strategie",
      description:
        "Wir identifizieren gemeinsam mit Ihnen die vielversprechendsten KI-Anwendungsfälle für Ihr Unternehmen.",
      features: [
        "Potenzialanalyse & Use-Case-Identifikation",
        "KI-Roadmap-Entwicklung",
        "Machbarkeitsstudien",
        "ROI-Berechnung",
        "Technologie-Auswahl",
      ],
    },
    {
      title: "Machine Learning Modelle",
      description:
        "Entwicklung und Training individueller Machine-Learning-Modelle für Prognosen, Klassifikation und Optimierung.",
      features: [
        "Datenaufbereitung und -analyse",
        "Modelltraining und -validierung",
        "Deployment in Produktivumgebungen",
        "Kontinuierliches Monitoring",
        "Modell-Optimierung",
      ],
    },
    {
      title: "KI-gestützte Automatisierung",
      description:
        "Kombination von KI und Prozessautomatisierung für intelligente, selbstlernende Workflows.",
      features: [
        "Intelligente Dokumentenverarbeitung",
        "Chatbots und virtuelle Assistenten",
        "Computer-Vision-Lösungen",
        "Anomalie- und Betrugserkennung",
        "Personalisierungs-Engines",
      ],
    },
  ];

  const faqs = [
    {
      question: "Welche Voraussetzungen brauche ich für den Einsatz von KI?",
      answer:
        "Grundsätzlich benötigen Sie ausreichend Daten sowie eine klare Zielsetzung. Wir prüfen gemeinsam mit Ihnen, welche Daten bereits vorhanden sind und welche Prozesse sich am besten für den KI-Einsatz eignen, auch wenn Sie noch am Anfang stehen.",
    },
    {
      question: "Wie lange dauert die Entwicklung einer KI-Lösung?",
      answer:
        "Je nach Komplexität dauert die Entwicklung von einfachen KI-Anwendungen 4-8 Wochen, während umfangreiche Machine-Learning-Projekte 3-6 Monate in Anspruch nehmen können. Wir liefern in agilen Iterationen frühzeitig erste Ergebnisse.",
    },
    {
      question: "Wie sicher sind meine Daten bei einer KI-Implementierung?",
      answer:
        "Datensicherheit und Datenschutz haben höchste Priorität. Wir setzen auf verschlüsselte Datenverarbeitung, DSGVO-konforme Prozesse und, wenn gewünscht, On-Premise- oder Private-Cloud-Lösungen.",
    },
    {
      question: "Welchen Mehrwert bringt KI konkret für mein Unternehmen?",
      answer:
        "Der Mehrwert hängt vom Anwendungsfall ab, reicht aber typischerweise von Kosteneinsparungen durch Automatisierung über verbesserte Entscheidungsfindung bis hin zu neuen, datengetriebenen Geschäftsmodellen.",
    },
    {
      question: "Muss ich bereits Erfahrung mit KI haben?",
      answer:
        "Nein, wir begleiten Sie von der ersten Idee bis zur produktiven Lösung. Unser Team übernimmt die technische Umsetzung, während wir Ihnen die Ergebnisse verständlich und praxisnah aufbereiten.",
    },
  ];

  return (
    <PageContainer>
      <SEO
        title="Künstliche Intelligenz (KI) Lösungen für Unternehmen"
        description="KI und Machine Learning für Ihr Unternehmen: Prozesse automatisieren, bessere Entscheidungen treffen, neue Geschäftschancen erschließen. Jetzt mit Byte Forge starten."
        path="/ki"
        keywords="Künstliche Intelligenz, KI Lösungen, Machine Learning, KI Beratung, KI Unternehmen"
      />
      <Container>
        <SectionTitle>
          <Trans>
            Künstliche Intelligenz für <span>Ihr Unternehmen</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Nutzen Sie das volle Potenzial von künstlicher Intelligenz und Machine
          Learning, um Prozesse zu automatisieren, bessere Entscheidungen zu
          treffen und neue Geschäftschancen zu erschließen.
        </SectionSubtitle>
      </Container>

      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>Künstliche Intelligenz</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>KI-Entwicklungsprozess</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>KI-Technologien</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>KI-Projekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      <ServiceTypesSection>
        <Container>
          <SectionTitle>
            Unsere <span>KI-Services</span>
          </SectionTitle>
          <ServiceTypeGrid>
            {serviceTypes.map((service, index) => (
              <ServiceTypeCard key={index}>
                <ServiceTypeTitle>{service.title}</ServiceTypeTitle>
                <ServiceTypeDescription>
                  {service.description}
                </ServiceTypeDescription>
                <ServiceTypeFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ServiceTypeFeatures>
              </ServiceTypeCard>
            ))}
          </ServiceTypeGrid>
        </Container>
      </ServiceTypesSection>

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit für den <span>Einstieg in KI?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam herausfinden, wie künstliche Intelligenz
            Ihr Unternehmen voranbringen kann.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>

      <RelatedServices currentPath="/ki" />
    </PageContainer>
  );
};

export default KI;
